import * as React from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faBookOpen, faBolt, faCircleQuestion, faClapperboard, faCubes, faDragon, faEye, faLayerGroup, faLink, faList, faRotate, faWindowMaximize } from '@fortawesome/free-solid-svg-icons'
import ChatbotWrapper from '../../../components/ChatbotWrapper'
import Section from '../../../components/Section'
import DownloadAndDiscordButtons from '../../../components/DownloadAndDiscordButtons'
import DiscordLink from '../../../components/DiscordLink'
import { RainbowText } from '../../../components/RainbowText'
import styles from './styles.module.scss'

type GuideCardProps = {
  title: string
  description: string
  icon: IconDefinition
  tag?: string
  href?: string
}

const DracoBot: React.FC<React.PropsWithChildren<{ reverse?: boolean; light?: boolean }>> = ({ reverse = true, light = true }) => {
  return <RainbowText light={light} reverse={reverse} text='Draco.Bot' />
}

const GuideCard: React.FC<GuideCardProps> = ({ title, description, icon, tag, href }) => {
  const content = (
    <>
      <div className={styles.cardHeader}>
        <FontAwesomeIcon icon={icon} className={styles.cardIcon} />
        <div>
          {tag && <div className={styles.cardTag}>{tag}</div>}
          <h3>{title}</h3>
        </div>
      </div>
      <p>{description}</p>
      <div className={styles.cardLink}>{href ? 'Open guide →' : 'Guide coming soon'}</div>
    </>
  )

  if (href) {
    return (
      <Link className={styles.guideCard} to={href}>
        {content}
      </Link>
    )
  }

  return <div className={styles.guideCard}>{content}</div>
}

const Learn: React.FC = () => {
  return (
    <ChatbotWrapper
      decorate
      containerWrap={false}
      title='Learn Draco.Bot'
      titleButtons={
        <div>
          <a href='#start' className={styles.iconButton} title='Start with what you want to do'>
            <FontAwesomeIcon icon={faBolt as IconDefinition} />
          </a>
          <a href='#building-blocks' className={styles.iconButton} title='Learn the building blocks'>
            <FontAwesomeIcon icon={faCubes as IconDefinition} />
          </a>
          <a href='#bites' className={styles.iconButton} title='Draco.Bites'>
            <FontAwesomeIcon icon={faDragon as IconDefinition} />
          </a>
        </div>
      }
      header={
        <>
          <p className='display-6'>You don&apos;t need to memorize every action before you can build something useful. Start with the result you want, then learn the pieces that make it work.</p>
          <p className='display-6'>
            These guides focus on real stream setups, from simple chat responses to reusable multi-step automation. If you get stuck, come ask in the <DiscordLink />.
          </p>
          <div className={styles.buttons}>
            <DownloadAndDiscordButtons />
          </div>
        </>
      }
    >
      <div className={styles.container}>
        <Section containerClassName={styles.noSmallGap} id='start' title='Start with what you want to do' icon={<FontAwesomeIcon icon={faBolt as IconDefinition} className={styles.sectionIcon} />}>
          <p className='display-6'>
            Most automations are easier to understand when you start with the outcome instead of the feature name. Pick something close to what you want, then adapt it to your stream.
          </p>

          <div className={styles.cardGrid}>
            <GuideCard
              icon={faClapperboard as IconDefinition}
              tag='Twitch + Variables'
              title='Create a clip and post it in chat'
              description='Create a Twitch clip, wait for Twitch to finish it, then use the finished clip URL in a follow-up chat message.'
              href='/chatbot/learn/create-clip-and-post-it'
            />
            <GuideCard
              icon={faEye as IconDefinition}
              tag='OBS + Timing'
              title='Show an OBS source for a set time'
              description='Turn a source on, wait for the amount of time you choose, then turn it back off as one reusable routine.'
              href='/chatbot/learn/show-obs-source-for-a-time'
            />
            <GuideCard
              icon={faRotate as IconDefinition}
              tag='Variables + Action Sets'
              title='Keep a separate counter for each viewer'
              description='Use variables inside variable names to remember things like how many times each viewer has been booped, bonked, hugged, or rewarded.'
              href='/chatbot/learn/per-viewer-counter'
            />
            <GuideCard
              icon={faList as IconDefinition}
              tag='Reusable Automation'
              title='Reuse the same actions in multiple places'
              description='Move repeated behavior into an Action Set, give it parameters, and call the same routine from commands, rewards, hotkeys, events, and more.'
              href='/chatbot/learn/reuse-actions'
            />
          </div>
        </Section>

        <Section
          containerClassName={styles.noSmallGap}
          id='building-blocks'
          title='Learn the building blocks'
          icon={<FontAwesomeIcon icon={faCubes as IconDefinition} className={styles.sectionIcon} />}
        >
          <p className='display-6'>
            <DracoBot /> is built around small pieces that can be combined. You can use only the basics, or layer them together when your stream needs more complicated behavior.
          </p>

          <div className={styles.cardGrid}>
            <GuideCard
              icon={faBolt as IconDefinition}
              tag='Start Here'
              title='Actions & action chains'
              description='Learn what an action is, where actions can be added, and how several actions run together from top to bottom.'
              href='/chatbot/learn/actions-and-chains'
            />
            <GuideCard
              icon={faLink as IconDefinition}
              title='Variables'
              description='Pass information from chat, Twitch, OBS, and earlier actions into later actions without hard-coding the same value everywhere.'
              href='/chatbot/learn/variables'
            />
            <GuideCard
              icon={faCircleQuestion as IconDefinition}
              title='Conditional Actions'
              description='Run different actions depending on a user, a variable, the current scene, or another condition in your stream.'
              href='/chatbot/learn/conditional-actions'
            />
            <GuideCard
              icon={faList as IconDefinition}
              title='Action Sets'
              description='Turn a useful group of actions into a reusable, parameterized routine that can be called from commands, events, rewards, and other automations.'
              href='/chatbot/learn/action-sets'
            />
            <GuideCard
              icon={faWindowMaximize as IconDefinition}
              title='Context Windows'
              description='Choose exactly when a nested group should see updated variables instead of the values captured when the original automation was queued.'
              href='/chatbot/learn/context-windows'
            />
            <GuideCard
              icon={faLayerGroup as IconDefinition}
              title='Queueing behavior'
              description='Understand unqueued, global, per-command/per-response, and custom queue groups so long routines stay predictable without unnecessary overlap.'
              href='/chatbot/learn/queueing'
            />
          </div>
        </Section>

        <Section containerClassName={styles.noSmallGap} id='bites' title='Draco.Bites' icon={<FontAwesomeIcon icon={faDragon as IconDefinition} className={styles.sectionIcon} />}>
          <div className={styles.bitesIntro}>
            <div>
              <div className={styles.comingSoon}>Coming soon</div>
              <p className='display-6'>
                Draco.Bites are bite-sized commands, Action Sets, and other ready-made automations you can import directly into <DracoBot />.
              </p>
              <p className='display-6'>
                They&apos;re meant to be useful immediately, but they&apos;re also examples you can open, inspect, and change. Import one because you want the result; learn how it works while you
                customize it.
              </p>
            </div>
            <div className={styles.biteExample}>
              <div className={styles.biteHeader}>
                <FontAwesomeIcon icon={faDragon as IconDefinition} />
                <div>
                  <span className={styles.biteLabel}>DRACO.BITE</span>
                  <strong>Per-User Interaction Counter</strong>
                </div>
              </div>
              <p>Reusable interaction commands with per-viewer counters, self-target protection, and first-time messaging.</p>
              <div className={styles.biteMeta}>
                <span>1 Action Set</span>
                <span>Variables</span>
                <span>Conditional Actions</span>
              </div>
              <button type='button' disabled>
                Import Bite Coming Soon
              </button>
            </div>
          </div>
        </Section>

        <Section
          noAlt
          containerClassName={styles.noSmallGap}
          contentContainerClassName={styles.finalSection}
          title='Build it once. Understand it forever.'
          icon={<FontAwesomeIcon icon={faBookOpen as IconDefinition} className={styles.sectionIcon} />}
        >
          <p className='display-6'>
            The goal isn&apos;t to turn every streamer into a programmer. It&apos;s to make powerful automation understandable enough that you can build what your stream needs and know why it works.
          </p>
          <div className={styles.buttons}>
            <DownloadAndDiscordButtons />
          </div>
        </Section>
      </div>
    </ChatbotWrapper>
  )
}

export default Learn
